import {
	useMutation,
	useQuery,
	useQueryClient,
	UseQueryOptions,
} from '@tanstack/react-query';
import { apis } from 'apis/apis';
import {
	IRecommandCollaborationDB,
	IRecommandCollaborationListDB,
	IRecommandCreatorListDB,
} from 'type/wconcepdb';

///////////////////////////////wconcepts
//getCreatorGroups
export const useGetRecommendationList = () => {
	return useQuery({
		queryKey: ['creator-recommandation-list'],
		queryFn: () => apis.getRecommendationList(),
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
	});
};
//getCreatorRecommendation
export const useGetRecommendationCreatorList = (
	recommendationId: string,
	platform: string,
) => {
	return useQuery({
		queryKey: ['creator-recommandation-creators-list', recommendationId, platform],
		queryFn: () => {
			if (!recommendationId || !platform)
				return { mega: [], macro: [], micro: [] } as IRecommandCreatorListDB;
			return apis.getRecommendationCreatorList(recommendationId, platform);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
	});
};

//getCreatorCollaborationList
export const useGetCreatorCollaborationList = (
	recommendationId: string,
	platform: string,
) => {
	return useQuery({
		queryKey: ['creator-recommandation-collaboration-list', recommendationId, platform],
		queryFn: () => {
			if (!recommendationId || !platform) return { mega: [], macro: [], micro: [] };
			return apis.getCreatorCollaborationList(recommendationId, platform);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
	});
};

export const useGetCreatorProfile = (recommendationId: string, creatorPk: string) => {
	return useQuery({
		queryKey: ['creator-profile', recommendationId, creatorPk],
		queryFn: () => apis.getCreatorProfile(recommendationId, creatorPk),
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
	});
};

//getCreatorContentsDetails
export const useGetCreatorContentsDetails = (
	recommendationId: string,
	creatorPk: string,
	contentPk: string,
) => {
	return useQuery({
		queryKey: ['creator-ContentsDetails', recommendationId, creatorPk, contentPk],
		queryFn: () => {
			if (!recommendationId || !creatorPk || !contentPk) {
				return null;
			}

			return apis.getCreatorContentsDetails(recommendationId, creatorPk, contentPk);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
	});
};

//putCreatorMemo
export const usePutCreatorMemo = (
	recommendationId: string,
	creatorPk: string,
	platform: string,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: ['creator-put-memo', recommendationId, creatorPk],
		mutationFn: (memo: string) => apis.putCreatorMemo(recommendationId, creatorPk, memo),
		onMutate: async (newMemo) => {
			const strRecommendationId = recommendationId.toString();

			await queryClient.cancelQueries({
				queryKey: [
					'creator-recommandation-collaboration-list',
					strRecommendationId,
					platform,
				],
			});

			const previousData = queryClient.getQueryData([
				'creator-recommandation-collaboration-list',
				strRecommendationId,
				platform,
			]);

			queryClient.setQueryData(
				['creator-recommandation-collaboration-list', strRecommendationId, platform],
				(old: IRecommandCollaborationListDB | undefined) => {
					if (!old) return old;

					const updateCreatorInList = (list: IRecommandCollaborationDB[]) =>
						list.map((creator) =>
							String(creator.creatorPk) === String(creatorPk)
								? { ...creator, memo: newMemo }
								: creator,
						);

					return {
						mega: updateCreatorInList(old.mega || []),
						macro: updateCreatorInList(old.macro || []),
						micro: updateCreatorInList(old.micro || []),
					};
				},
			);

			return { previousData };
		},
		onError: (err, newMemo, context) => {
			queryClient.setQueryData(
				[
					'creator-recommandation-collaboration-list',
					recommendationId.toString(),
					creatorPk,
				],
				context?.previousData,
			);
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: [
					'creator-recommandation-collaboration-list',
					recommendationId.toString(),
					creatorPk,
				],
			});
		},
		retry: 0,
	});
};
